import { useEffect, useState } from "react";
import { Mail, Save, Send, Palette } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { PageHeader, Panel, Field, SelectField, Empty } from "../components/common";
import { api } from "../context/DataContext";
import { HEURES, TZ_LABELS } from "../lib/constants";

const SWATCHES = [["Anthracite", "#1B1B1B"], ["Or", "#C9B05A"], ["Or foncé", "#9C8535"], ["Blanc", "#F8F8F8"], ["Fond", "#F7F6F2"]];

export default function Reglages() {
  const [s, setS] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const load = () => Promise.all([api.get("/settings/digest"), api.get("/digest/today")]).then(([a, b]) => { setS(a.data); setPreview(b.data); });
  useEffect(() => { load(); }, []);
  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/settings/digest", { email: s.email, heure: s.heure, tz: s.tz, actif: s.actif });
      setS(data);
      toast.success("Réglages enregistrés", { description: data.actif ? `Récap envoyé à ${data.email} chaque jour à ${data.heure.replace(":", " h ")}` : "Envoi quotidien désactivé" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    setBusy(false);
  };
  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`E-mail de test envoyé à ${data.to}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Échec de l'envoi"); }
    setSending(false);
  };

  if (!s) return <p className="text-[var(--kc-muted)]" data-testid="reglages-loading">Chargement…</p>;
  const tzOptions = s.timezones.map((id) => ({ id, label: TZ_LABELS[id] || id }));

  return (
    <div data-testid="reglages-page">
      <PageHeader eyebrow="Paramètres" title="Réglages" description="Récapitulatif quotidien par e-mail et identité visuelle du cabinet." />
      <div className="grid gap-6 xl:grid-cols-5">
        <Panel testId="reglages-digest-panel" className="xl:col-span-3" title="Récapitulatif quotidien par e-mail">
          <form onSubmit={save} className="space-y-6">
            <div className="flex items-center justify-between rounded-lg border border-[var(--kc-line)] bg-[var(--kc-bg)] px-4 py-3">
              <div>
                <p className="text-sm font-medium text-[var(--kc-navy)]">Envoi automatique chaque matin</p>
                <p className="text-xs text-[var(--kc-muted)]">Rendez-vous, relances du jour, retards et livrables à échéance.</p>
              </div>
              <Switch checked={s.actif} onCheckedChange={set("actif")} data-testid="reglages-actif-switch" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Adresse e-mail destinataire" className="sm:col-span-2"><Input type="email" value={s.email} onChange={set("email")} data-testid="reglages-email-input" /></Field>
              <Field label="Heure d'envoi"><SelectField testId="reglages-heure-select" value={s.heure} onChange={set("heure")} options={HEURES} /></Field>
              <Field label="Fuseau horaire"><SelectField testId="reglages-tz-select" value={s.tz} onChange={set("tz")} options={tzOptions} /></Field>
            </div>
            <div className="flex flex-wrap justify-end gap-3 border-t border-[var(--kc-line)] pt-5">
              <Button type="button" variant="outline" onClick={sendTest} disabled={sending} data-testid="reglages-send-test-button"><Send className="mr-1 h-4 w-4" />{sending ? "Envoi…" : "Envoyer un test"}</Button>
              <Button type="submit" disabled={busy} className="kc-btn-primary" data-testid="reglages-save-button"><Save className="mr-1 h-4 w-4" />Enregistrer</Button>
            </div>
          </form>
        </Panel>

        <div className="space-y-6 xl:col-span-2">
          <Panel testId="reglages-preview-panel" title="Aperçu du récap d'aujourd'hui">
            {preview ? (
              <div className="space-y-4 text-sm">
                <p className="capitalize text-[var(--kc-muted)]" data-testid="reglages-preview-date">{preview.date_label}</p>
                <div className="grid grid-cols-3 gap-3">
                  {[["Aujourd'hui", preview.jour.length, "jour"], ["En retard", preview.retard.length, "retard"], ["Livrables", preview.livrables.length, "livrables"]].map(([l, n, id]) => (
                    <div key={id} className="rounded-lg bg-[var(--kc-sand)] p-3 text-center">
                      <p className="font-display text-2xl font-semibold text-[var(--kc-navy)]" data-testid={`reglages-preview-${id}`}>{n}</p>
                      <p className="text-[11px] uppercase tracking-wider text-[var(--kc-muted)]">{l}</p>
                    </div>
                  ))}
                </div>
                <ul className="space-y-1.5">
                  {preview.jour.map((r) => <li key={r.id} className="flex gap-2 text-[var(--kc-ink)]"><Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--kc-gold-dark)]" /><span><span className="font-mono text-xs">{r.date.slice(11, 16)}</span> {r.titre} <span className="text-[var(--kc-muted)]">— {r.entreprise}</span></span></li>)}
                  {!preview.jour.length && <Empty>Rien de prévu aujourd'hui.</Empty>}
                </ul>
              </div>
            ) : <Empty>Chargement…</Empty>}
          </Panel>

          <Panel testId="reglages-charte-panel" title="Identité visuelle">
            <div className="flex items-center gap-4">
              <div className="rounded-md bg-[var(--kc-navy)] p-3"><img src="/logo-mark.png" alt="Logo" className="h-12 w-auto" /></div>
              <div className="text-sm">
                <p className="font-medium text-[var(--kc-navy)]">Logo officiel du cabinet</p>
                <p className="text-xs text-[var(--kc-muted)]">Charte appliquée : anthracite, or et blanc, d'après le logo fourni.</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              {SWATCHES.map(([n, c]) => (
                <div key={c} className="flex items-center gap-2 text-xs">
                  <span className="h-6 w-6 rounded-md border border-[var(--kc-line)]" style={{ background: c }} />
                  <span><span className="font-medium text-[var(--kc-navy)]">{n}</span> <span className="font-mono text-[var(--kc-muted)]">{c}</span></span>
                </div>
              ))}
            </div>
            <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-[var(--kc-muted)]"><Palette className="h-3.5 w-3.5" />Pour changer le logo : remplacer les fichiers logo-mark.png et logo-full.png.</p>
          </Panel>
        </div>
      </div>
    </div>
  );
}
